import { useEffect, useState } from 'react'
import { useList } from '@refinedev/core'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Pagination,
  Typography,
  Box,
} from '@mui/material'

interface OpportunityUUIDList {
  filteredOpportunitiesUUID: string[]
}

export default function TestFiltersOpportunitiesList({
  filteredOpportunitiesUUID,
}: OpportunityUUIDList) {
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const { data, isLoading, isError, refetch } = useList({
    resource: 'opportunities',
    filters: [
      {
        field: 'uuid',
        operator: 'in',
        value: filteredOpportunitiesUUID,
      },
    ],
    pagination: {
      current: currentPage,
      pageSize: pageSize,
    },
    meta: { isTestFiltered: true },
    queryOptions: { enabled: filteredOpportunitiesUUID.length > 0 },
  })

  useEffect(() => {
    if (filteredOpportunitiesUUID.length > 0) {
      refetch()
    }
  }, [filteredOpportunitiesUUID, refetch])

  const total = data?.total || 0
  const totalPages = Math.ceil(total / pageSize)

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ): void => {
    setCurrentPage(page)
    refetch()
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return <Typography color="error">Error loading opportunities</Typography>
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
        Filtered Opportunities
      </Typography>

      <Typography variant="body1" gutterBottom>
        Total Opportunities: {total}
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Funder and Fund</strong>
              </TableCell>
              <TableCell>
                <strong>Amount</strong>
              </TableCell>
              <TableCell>
                <strong>Timing Details</strong>
              </TableCell>
              <TableCell>
                <strong>Always Open?</strong>
              </TableCell>
              <TableCell>
                <strong>Locations</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.data.map((opportunity) => (
              <TableRow key={opportunity.uuid}>
                <TableCell>
                  {opportunity.funder_name} -{' '}
                  {opportunity.fund_name
                    ? opportunity.fund_name
                    : '** No Fund Name **'}
                </TableCell>
                <TableCell>
                  {opportunity.amount
                    ? opportunity.amount
                    : '** No Amount Specified **'}
                </TableCell>
                <TableCell>
                  {opportunity.timing
                    ? opportunity.timing
                    : '** No Timing Specified **'}
                </TableCell>
                <TableCell>{opportunity.always_open ? 'Yes' : 'No'}</TableCell>
                <TableCell sx={{ minWidth: '200px', overflow: 'auto' }}>
                  {opportunity.opportunity_region_names.length > 0
                    ? opportunity.opportunity_region_names.join(', ')
                    : '** No Locations Specified for funder or opportunity **'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  )
}
